import { parseRoute } from "../router";

import type { ClientReportRequest } from "@canvaschool/contracts";

/** What a report carries about the minutes before it: identifiers, codes and
 * error text only — never drawing content. */
export type ClientEvent = { at: string; type: string; text: string };

const EVENT_LIMIT = 50;
const MAX_ERRORS_PER_LOAD = 10;
/** Browser noise with no user-visible effect. */
const IGNORED = /^ResizeObserver loop/;

const events: ClientEvent[] = [];

export const recordClientEvent = (type: string, text: string): void => {
  events.push({
    at: new Date().toISOString(),
    type,
    text: text.slice(0, 300),
  });
  if (events.length > EVENT_LIMIT) {
    events.shift();
  }
};

export const clearClientEvents = (): void => {
  events.length = 0;
};

/** The route's ids and the recent events, for any report sent from here. */
export const reportContext = (): Pick<
  ClientReportRequest,
  "classId" | "canvasId"
> & { detail: NonNullable<ClientReportRequest["detail"]> } => {
  const route = parseRoute(window.location.pathname);
  return {
    ...("classId" in route ? { classId: route.classId } : {}),
    ...("canvasId" in route ? { canvasId: route.canvasId } : {}),
    detail: {
      path: window.location.pathname,
      screen: `${window.innerWidth}x${window.innerHeight}`,
      events: events.slice(),
    },
  };
};

/**
 * Sends uncaught errors and unhandled rejections to operators: each distinct
 * message once per page load, at most ten per load, so one broken render loop
 * cannot flood. Returns the uninstaller.
 */
export const installErrorCapture = (
  send: (report: ClientReportRequest) => Promise<void>,
): (() => void) => {
  const sent = new Set<string>();
  const capture = (kind: string, text: string, stack?: string) => {
    const message = (text || "unknown error").slice(0, 2000);
    if (IGNORED.test(message)) {
      return;
    }
    const first = !sent.has(message) && sent.size < MAX_ERRORS_PER_LOAD;
    if (first) {
      sent.add(message);
      const context = reportContext();
      send({
        ...context,
        source: "ERROR",
        kind: kind.slice(0, 80),
        message,
        detail: {
          ...context.detail,
          ...(stack ? { stack: stack.slice(0, 4000) } : {}),
        },
      }).catch(() => undefined);
    }
    recordClientEvent("error", message);
  };
  const onError = (event: ErrorEvent) =>
    capture(
      event.error instanceof Error ? event.error.name : "Error",
      event.message,
      event.error instanceof Error ? event.error.stack : undefined,
    );
  const onRejection = (event: PromiseRejectionEvent) => {
    const { reason } = event;
    capture(
      "unhandledrejection",
      reason instanceof Error
        ? `${reason.name}: ${reason.message}`
        : String(reason),
      reason instanceof Error ? reason.stack : undefined,
    );
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
};
